import React, { useEffect, useState, useCallback } from 'react';
import { Search, Plus } from 'lucide-react';
import { salesReturnsApi } from '../api/salesReturns';
import { salesApi } from '../api/sales';
import { purchaseApi } from '../api/purchase';
import { useToast } from '../hooks/useToast';

const colorMap: Record<string,string> = { blue:'badge-blue', green:'badge-green', amber:'badge-amber', gray:'badge-gray', red:'badge-red' };

const SalesReturns: React.FC = () => {
  const { showToast } = useToast();
  const [returns, setReturns]   = useState<any[]>([]);
  const [total, setTotal]       = useState(0);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [sales, setSales]       = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [selectedSale, setSelectedSale] = useState('');
  const [saleData, setSaleData] = useState<any>(null);
  const [returnLines, setReturnLines] = useState<any[]>([]);
  const [form, setForm] = useState({ branch_id:'', return_date: new Date().toISOString().split('T')[0], reason:'', refund_account_id:'', return_location_id:'' });
  const [accounts, setAccounts] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    salesReturnsApi.list({ search }).then(res => { setReturns(res.data.data); setTotal(res.data.total); setLoading(false); });
  }, [search]);

  useEffect(() => {
    salesApi.list({ search:'' }).then(r => setSales(r.data.data || []));
    import('../api/coa').then(m => m.coaApi.list().then(res => setAccounts(res.data.data.filter((a:any) => a.subtype_name === 'Current Asset'))));
    import('../api/purchase').then(m => m.purchaseApi.locations().then(res => setLocations(res.data.data)));
    purchaseApi.branches().then(r => setBranches(r.data.data));
  }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const handleSaleSelect = async (saleId: string) => {
    setSelectedSale(saleId); setSaleData(null); setReturnLines([]);
    if (!saleId) return;
    const res = await salesReturnsApi.getSaleItems(Number(saleId));
    setSaleData(res.data.data);
    setReturnLines(res.data.data.items.map((item: any) => ({
      sale_item_id: item.id,
      product_id:   item.product_id,
      location_id:  item.location_id,
      product_name: item.product_name,
      sku:          item.sku,
      unit_symbol:  item.unit_symbol,
      location_name:item.location_name,
      qty:          item.qty,
      qty_available:item.qty_available_to_return,
      unit_price:   item.unit_price,
      unit_cost:    item.unit_cost,
      unit_discount: item.qty > 0 ? (item.discount || 0) / item.qty : 0,
      qty_return:   0,
      include:      false,
    })));
  };

  const updateLine = (i: number, field: string, val: any) => {
    setReturnLines(l => l.map((line, idx) => idx===i ? {...line,[field]:val} : line));
  };

  const handleSubmit = async () => {
    const items = returnLines.filter(l => l.include && l.qty_return > 0);
    if (!items.length) { showToast('Select at least one item to return', 'error'); return; }
    if (!form.branch_id) { showToast('Branch required', 'error'); return; }
    setSaving(true);
    try {
      const res = await salesReturnsApi.createReturn({
        sale_id:     Number(selectedSale),
        contact_id:  saleData.sale.contact_id,
        branch_id:   Number(form.branch_id),
        return_date: form.return_date,
        reason:      form.reason,
        refund_account_id: form.refund_account_id ? Number(form.refund_account_id) : null,
        return_location_id: form.return_location_id ? Number(form.return_location_id) : null,
        items:       items.map(l => ({ sale_item_id:l.sale_item_id, product_id:l.product_id, location_id:l.location_id, qty_return:Number(l.qty_return), unit_price:Number(l.unit_price), unit_cost:Number(l.unit_cost), unit_discount:Number(l.unit_discount||0) })),
      });
      showToast(res.data.message);
      setShowForm(false); setSelectedSale(''); setSaleData(null); setReturnLines([]);
      load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const grandTotal = returnLines.filter(l => l.include).reduce((s,l) => s + (Number(l.qty_return)*Number(l.unit_price)), 0);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Sales Returns</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}><Plus size={13}/> New Return</button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>New Sales Return</span><button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-3" style={{ marginBottom:14 }}>
              <div className="form-group"><label className="form-label">Invoice *</label>
                <select className="form-control" value={selectedSale} onChange={e => handleSaleSelect(e.target.value)}>
                  <option value="">Select Invoice</option>
                  {sales.map((s:any) => <option key={s.id} value={s.id}>{s.sale_no} — {s.customer_name||'Walk-in'}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Branch *</label>
                <select className="form-control" value={form.branch_id} onChange={e => setForm(f=>({...f,branch_id:e.target.value}))}>
                  <option value="">Select branch</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Return Date *</label>
                <input type="date" className="form-control" value={form.return_date} onChange={e => setForm(f=>({...f,return_date:e.target.value}))} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom:14 }}>
              <label className="form-label">Reason</label>
              <input className="form-control" value={form.reason} onChange={e => setForm(f=>({...f,reason:e.target.value}))} placeholder="e.g. Defective product, wrong item..." />
            </div>
            <div className="form-grid-2">
              <div className="form-group"><label className="form-label">Refund Account (leave blank to reduce AR)</label>
                <select className="form-control" value={form.refund_account_id} onChange={e => setForm(f=>({...f,refund_account_id:e.target.value}))}>
                  <option value="">Reduce AR (credit note)</option>
                  {accounts.map((a:any) => <option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Return to Location</label>
                <select className="form-control" value={form.return_location_id} onChange={e => setForm(f=>({...f,return_location_id:e.target.value}))}>
                  <option value="">Same as original</option>
                  {locations.map((l:any) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            </div>

            {returnLines.length > 0 && (
              <>
                <div style={{ marginBottom:8, fontSize:12, fontWeight:600, color:'var(--tx2)' }}>Select items to return:</div>
                <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:8 }}>
                  <thead><tr style={{ background:'var(--sur2)' }}>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>✓</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>SKU</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Product</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Location</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Sold</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Available</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Return Qty</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Unit Price</th>
                    <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Total</th>
                  </tr></thead>
                  <tbody>
                    {returnLines.map((line, i) => (
                      <tr key={i} style={{ borderBottom:'1px solid var(--bdr)', opacity: line.qty_available <= 0 ? 0.4 : 1 }}>
                        <td style={{ padding:'6px 10px' }}><input type="checkbox" checked={line.include} disabled={line.qty_available <= 0} onChange={e => updateLine(i,'include',e.target.checked)} /></td>
                        <td style={{ padding:'6px 10px', fontFamily:'monospace', fontSize:12, color:'var(--blue)' }}>{line.sku}</td>
                        <td style={{ padding:'6px 10px', fontSize:12 }}>{line.product_name}</td>
                        <td style={{ padding:'6px 10px', fontSize:11, color:'var(--tx3)' }}>{line.location_name}</td>
                        <td style={{ padding:'6px 10px', textAlign:'right', fontFamily:'monospace', fontSize:12 }}>{line.qty}</td>
                        <td style={{ padding:'6px 10px', textAlign:'right', fontFamily:'monospace', fontSize:12, color:'var(--green)' }}>{line.qty_available}</td>
                        <td style={{ padding:'6px 4px', width:90 }}>
                          <input type="number" className="form-control mono" value={line.qty_return} max={line.qty_available} min={0} disabled={!line.include}
                            onChange={e => updateLine(i,'qty_return',Math.min(Number(e.target.value),line.qty_available))} style={{ textAlign:'right' }} />
                        </td>
                        <td style={{ padding:'6px 10px', textAlign:'right', fontFamily:'monospace', fontSize:12 }}>{Number(line.unit_price).toFixed(2)}</td>
                        <td style={{ padding:'6px 10px', textAlign:'right', fontFamily:'monospace', fontWeight:600, fontSize:12, color:'var(--red)' }}>{(Number(line.qty_return)*Number(line.unit_price)).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ fontFamily:'monospace', fontWeight:700, fontSize:14, color:'var(--red)' }}>Return Total: {grandTotal.toFixed(2)}</div>
                  <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Posting...' : 'Post Return'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="filter-bar">
        <div className="search-box"><Search size={13} color="var(--tx3)"/><input placeholder="Search returns..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <span className="record-count">{total} records</span>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Return No</th><th>Customer</th><th style={{textAlign:'right'}}>Amount</th><th>Status</th><th>Date</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} style={{textAlign:'center',padding:32,color:'var(--tx3)'}}>Loading...</td></tr>
            : returns.length === 0 ? <tr><td colSpan={5}><div className="empty-state"><p>No sales returns yet.</p></div></td></tr>
            : returns.map(r => (
              <tr key={r.id}>
                <td className="td-code">{r.return_no}</td>
                <td style={{fontWeight:600}}>{r.customer_name}</td>
                <td className="td-num td-red">{Number(r.total_amount).toFixed(2)}</td>
                <td><span className={`badge ${colorMap[r.status_color]||'badge-gray'}`}>{r.status}</span></td>
                <td style={{color:'var(--tx3)',fontSize:11}}>{new Date(r.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default SalesReturns;
